/* Owns permission checks in the panel. It is a thin, deliberately logic-free
   layer over `can()` from @sentinelac/shared: the API's `requireRole`
   preHandler calls the same function, so the UI and the server can never
   disagree about who may do what (SPEC §4.4). */

import { can, PERMISSION_KEYS, permissionsFor, type Permission, type Role } from '@sentinelac/shared'

import { useAuthState } from './auth-store.js'

export { can, permissionsFor, PERMISSION_KEYS }
export type { Permission, Role }

/** `null` role = no membership on that server, which can never satisfy a permission. */
export function canRole(role: Role | null | undefined, permission: Permission): boolean {
  if (!role) return false
  return can(role, permission)
}

/** The caller's role on `serverId`, or on the active server when it is omitted. */
export function useRole(serverId?: string | null): Role | null {
  const { servers, activeServerId } = useAuthState()
  const id = serverId === undefined ? activeServerId : serverId
  if (!id) return null
  return servers.find((s) => s.id === id)?.role ?? null
}

export function useCan(permission: Permission, serverId?: string | null): boolean {
  return canRole(useRole(serverId), permission)
}

/**
 * Every permission the role holds, precomputed for the `RoleMatrix` and for
 * components that check several permissions in one render.
 */
export function useRolePermissions(serverId?: string | null): ReadonlySet<Permission> {
  const role = useRole(serverId)
  return new Set(role ? permissionsFor(role) : [])
}

export function useIsPlatformAdmin(): boolean {
  return useAuthState().user?.isPlatformAdmin === true
}
